import React, { useState, useMemo } from 'react';
import { Checkbox, Button, Select, Space } from 'antd';
import { cloneDeep } from 'lodash';

export interface Option {
  label: string;
  value: number;
}

interface CustomMultiSelectProps {
  value?: any[];
  onChange?: (selectedIds: number[]) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  emptyText?: string;
  selectedIds?: any[];
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  selectedIds = [],
  onChange,
  options,
  placeholder = 'Select',
  disabled = false,
  loading = false,
  emptyText = 'No options available',
}) => {
  const [open, setOpen] = useState(false);
  const [currentSelectedValues, setCurrentSelectedValues] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');

  const filteredOptions = useMemo(() => {
    if (options.length === 0) {
      return [];
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [options, searchText]);

  // Create options with custom checkbox labels
  const optionsWithCheckboxes = useMemo(() => {
    return filteredOptions.map((option) => {
      const isSelected = selectedIds.includes(option.value);
      const isCurrentlySelected = currentSelectedValues.includes(option.value);

      return {
        label: (
          <Checkbox
            checked={isCurrentlySelected}
            onChange={(e) => {
              const newValue = e.target.checked
                ? [...currentSelectedValues, option.value]
                : currentSelectedValues.filter((val) => val !== option.value);
              setCurrentSelectedValues(newValue);
            }}
          >
            <span>{option.label}</span>
            {isSelected && (
              <span
                style={{ color: 'rgba(0,0,0,.45)', display: 'inline-block', marginLeft: '8px' }}
              >
                Added
              </span>
            )}
          </Checkbox>
        ),
        value: option.value,
        key: option.value,
      };
    });
  }, [filteredOptions, selectedIds, currentSelectedValues]);

  const handleCancel = () => {
    setCurrentSelectedValues(cloneDeep(selectedIds));
    setSearchText('');
    setOpen(false);
  };

  const handleApply = () => {
    onChange?.(currentSelectedValues);
    setSearchText('');
    setOpen(false);
  };

  return (
    <Select
      loading={loading}
      mode="multiple"
      style={{ width: '100%' }}
      placeholder={placeholder}
      options={optionsWithCheckboxes}
      showSearch
      allowClear={false}
      searchValue={searchText}
      value={[]}
      disabled={disabled}
      notFoundContent={loading ? 'Loading...' : emptyText}
      dropdownRender={(menu) => (
        <div>
          <div>{menu}</div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: 8,
              borderTop: '1px solid #e8e8e8',
            }}
          >
            <Space>
              <Button size="small" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="primary" size="small" onClick={handleApply}>
                Apply
              </Button>
            </Space>
          </div>
        </div>
      )}
      open={open}
      onSearch={(val) => setSearchText(val)}
      onDropdownVisibleChange={(open) => {
        if (!open) {
          setCurrentSelectedValues(cloneDeep(selectedIds));
          setSearchText('');
        } else {
          setCurrentSelectedValues(cloneDeep(selectedIds));
        }
        setOpen(open);
      }}
      filterOption={false}
    />
  );
};

export default CustomMultiSelect;
